import React from 'react';
import {
  Gauge, Scales, Target, ThermometerSimple, ChartLineUp, Compass,
  WarningCircle, CheckCircle,
} from '@phosphor-icons/react';

/**
 * KpiStrip — hero-screen KPI row for the tri-node equilibrium (Parts 3-5 + AP-CAM NSS).
 *
 * Reads the *raw* part3/part4/part5 objects (parts.part3_equilibrium etc. from
 * useProjectAnalysis), not the flattened `apcas` view-model that backfills missing
 * values with 0 — so a genuinely absent KPI renders "n/d" instead of a fake 0.
 * `apcas` here is only used for the NSS saturation reading (block5.nss_nominal /
 * nss_pct), which is why it stays a separate prop from part3-part5.
 */

export interface KpiStripProps {
  /** parts.part3_equilibrium — controlling_capacity (CC, mov/h), bci (0..1), svs / svs_pct */
  part3: any;
  /** parts.part4_indicators — is_schedule_unsustainable / is_system_imbalanced / is_apron_the_limiter */
  part4: any;
  /** parts.part5_kpis — amsri / amsri_pct, ips / ips_pct */
  part5: any;
  /** useProjectAnalysis().apcas (flattened) — used only for nss_saturation / nss_pct */
  apcas: any;
  isDark: boolean;
  language: 'es' | 'en';
}

const fmtNum = (val: any, decimals = 1): string => {
  if (val === null || val === undefined || val === '') return 'n/d';
  const n = typeof val === 'number' ? val : parseFloat(val);
  if (Number.isNaN(n)) return 'n/d';
  return n.toFixed(decimals);
};

const fmtPct = (val: any, decimals = 1): string => {
  const s = fmtNum(val, decimals);
  return s === 'n/d' ? s : `${s}%`;
};

/** *_pct is already a percentage and takes priority; falls back to score*100 when only the 0..1 index is present. */
const pctFrom = (pctVal: any, scoreVal: any, decimals = 1): string => {
  if (pctVal !== null && pctVal !== undefined && pctVal !== '' && !Number.isNaN(parseFloat(pctVal))) {
    return fmtPct(pctVal, decimals);
  }
  if (scoreVal !== null && scoreVal !== undefined && scoreVal !== '' && !Number.isNaN(parseFloat(scoreVal))) {
    return fmtPct(parseFloat(scoreVal) * 100, decimals);
  }
  return 'n/d';
};

interface KpiCardDef {
  key: string;
  label: string;
  value: string;
  icon: React.ReactNode;
}

type Severity = 'danger' | 'warning' | 'info';

interface DiagnosticDef {
  key: string;
  flag: boolean;
  known: boolean;
  labelOn: string;
  labelOff: string;
  severity: Severity;
}

const KpiStrip: React.FC<KpiStripProps> = ({ part3, part4, part5, apcas, isDark, language }) => {
  void isDark; // surfaces are theme-aware via CSS custom properties (--aipron-*), no branching needed
  const isEs = language === 'es';

  const p3 = part3 || {};
  const p4 = part4 || {};
  const p5 = part5 || {};
  const ap = apcas || {};

  const cards: KpiCardDef[] = [
    {
      key: 'cc',
      label: isEs ? 'CC · Capacidad controlante' : 'CC · Controlling capacity',
      value: p3.controlling_capacity !== null && p3.controlling_capacity !== undefined
        ? `${fmtNum(p3.controlling_capacity, 0)} mov/h`
        : 'n/d',
      icon: <Gauge size={15} weight="duotone" />,
    },
    {
      key: 'bci',
      label: isEs ? 'BCI · Índice de balance' : 'BCI · Balance index',
      value: fmtNum(p3.bci, 2),
      icon: <Scales size={15} weight="duotone" />,
    },
    {
      key: 'svs',
      label: isEs ? 'SVS · Viabilidad de slots' : 'SVS · Slot viability',
      value: pctFrom(p3.svs_pct, p3.svs),
      icon: <Target size={15} weight="duotone" />,
    },
    {
      key: 'nss',
      label: isEs ? 'NSS · Saturación' : 'NSS · Saturation',
      value: pctFrom(ap.nss_pct, ap.nss_nominal ?? ap.nss_saturation),
      icon: <ThermometerSimple size={15} weight="duotone" />,
    },
    {
      key: 'amsri',
      label: 'AMSRI',
      value: pctFrom(p5.amsri_pct, p5.amsri),
      icon: <ChartLineUp size={15} weight="duotone" />,
    },
    {
      key: 'ips',
      label: 'IPS',
      value: pctFrom(p5.ips_pct, p5.ips),
      icon: <Compass size={15} weight="duotone" />,
    },
  ];

  const diagnostics: DiagnosticDef[] = [
    {
      key: 'unsustainable',
      flag: !!p4.is_schedule_unsustainable,
      known: p4.is_schedule_unsustainable !== undefined && p4.is_schedule_unsustainable !== null,
      labelOn: isEs ? 'Horario insostenible' : 'Schedule unsustainable',
      labelOff: isEs ? 'Horario sostenible' : 'Schedule sustainable',
      severity: 'danger',
    },
    {
      key: 'imbalanced',
      flag: !!p4.is_system_imbalanced,
      known: p4.is_system_imbalanced !== undefined && p4.is_system_imbalanced !== null,
      labelOn: isEs ? 'Sistema desequilibrado' : 'System imbalanced',
      labelOff: isEs ? 'Sistema equilibrado' : 'System balanced',
      severity: 'warning',
    },
    {
      key: 'apron_limiter',
      flag: !!p4.is_apron_the_limiter,
      known: p4.is_apron_the_limiter !== undefined && p4.is_apron_the_limiter !== null,
      labelOn: isEs ? 'Apron es el limitante' : 'Apron is the limiter',
      labelOff: isEs ? 'Apron no es el limitante' : 'Apron is not the limiter',
      severity: 'info',
    },
  ];

  const knownDiagnostics = diagnostics.filter((d) => d.known);

  return (
    <div className="w-full">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((c) => (
          <div key={c.key} className="kpi-card">
            <div className="flex items-center gap-1.5 text-[var(--text-tertiary)] mb-1">
              {c.icon}
              <span className="kpi-label">{c.label}</span>
            </div>
            <div className="kpi-value" style={{ fontSize: '1.375rem' }}>{c.value}</div>
          </div>
        ))}
      </div>

      {knownDiagnostics.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {knownDiagnostics.map((d) => {
            const badgeClass = d.flag
              ? d.severity === 'danger' ? 'badge-danger' : d.severity === 'warning' ? 'badge-warning' : 'badge-info'
              : 'badge-success';
            return (
              <span key={d.key} className={`badge ${badgeClass}`}>
                {d.flag ? <WarningCircle size={12} weight="bold" /> : <CheckCircle size={12} weight="bold" />}
                {d.flag ? d.labelOn : d.labelOff}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KpiStrip;
